import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInputListItem from 'LuxKit/List/RadioInputListItem'
import FormModal from 'LuxKit/Modal/FormModal'
import ModalContext from 'contexts/ModalContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { capitalise } from 'lib/string/stringUtils'
import React, { useCallback, useContext, useMemo, useRef } from 'react'

export const merchandiseSearchSortOptions: Array<{ label: string, value: App.MerchandiseSearchSort }> = [
  { label: 'Recommended', value: 'recommended' },
  { label: 'New Arrivals', value: 'newArrivals' },
  { label: 'Price (lowest)', value: 'priceAsc' },
  { label: 'Price (highest)', value: 'priceDesc' },
]

interface Props {
  /**
   * The current sort by value
   *
   * @default recommended
   */
  sortBy?: App.MerchandiseSearchSort
  onChange?: (sortBy: App.MerchandiseSearchSort) => void
}

function MerchandiseSearchPageSort(props: Props) {
  const { sortBy = 'recommended', onChange } = props
  const [openDropdown, toggleDropdown, _, closeDropdown] = useToggle()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')

  const onFilterClick = useCallback(async() => {
    if (isMobile) {
      const result = await showModal<{ sortBy: App.MerchandiseSearchSort }>(resolve => <FormModal title="Sort by" flush>
        {merchandiseSearchSortOptions.map((option) => <RadioInputListItem
          key={option.value}
          name="sortBy"
          title={option.label}
          value={option.value}
          defaultChecked={sortBy === option.value}
          onChange={e => resolve({ sortBy: e.target.value as App.MerchandiseSearchSort })}
          inputPlacement="start"
        />)}
      </FormModal>)

      if (result?.sortBy) {
        onChange?.(result.sortBy)
      }
    } else {
      toggleDropdown()
    }
  }, [isMobile, onChange, showModal, sortBy, toggleDropdown])

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value as App.MerchandiseSearchSort)
    closeDropdown()
  }, [onChange, closeDropdown])

  const selectedSortLabel = useMemo(() => {
    return merchandiseSearchSortOptions.find(option => option.value === sortBy)?.label ?? ''
  }, [sortBy])

  return <>
    <FilterChip
      size="medium"
      ref={anchorRef}
      onClick={onFilterClick}
      endIcon={<DropdownChevron open={openDropdown} />}
    >
      Sort: {capitalise(selectedSortLabel)}
    </FilterChip>
    <DropdownList
      open={openDropdown}
      anchorRef={anchorRef}
      triggerRef={anchorRef}
      size="S"
      onClose={closeDropdown}
    >
      {merchandiseSearchSortOptions.map((option) => <RadioInputListItem
        key={option.value}
        name="sortBy"
        title={option.label}
        value={option.value}
        defaultChecked={sortBy === option.value}
        inputPlacement="start"
        onChange={onSortChange}
      />)}
    </DropdownList>
  </>
}

export default MerchandiseSearchPageSort
